import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowLeft, ShieldCheck, Mail, IdCard } from "lucide-react";
import mx from "@/i18n/mx.json";

/**
 * Thank You page — Spanish only, mounted at /thank_you.
 * Content is loaded directly from the Spanish dictionary to guarantee
 * that the user never sees English here, regardless of prior locale.
 */
export default function ThankYou() {
  useEffect(() => {
    document.documentElement.lang = "es-MX";
    document.title = "¡Gracias! · Paynoravello";
  }, []);

  const t = (key) => key.split(".").reduce((acc, part) => acc?.[part], mx) || key;

  return (
    <div className="min-h-screen bg-[color:var(--pn-off-white)] flex flex-col" data-testid="thank-you-page">
      <div className="bg-[#0B2545] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="thank-you-logo">
            <span className="grid place-items-center h-9 w-9 rounded-md bg-[#006837] text-white">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="font-serif-heading text-base font-bold">{t("common.companyName")}</span>
          </Link>
          <Link
            to="/"
            data-testid="thank-you-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t("thankYou.cta")}
          </Link>
        </div>
      </div>

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center" data-testid="thank-you-confirmation-container">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#E8F5E9] text-[#006837]">
            <CheckCircle2 className="h-9 w-9" aria-hidden="true" />
          </div>
          <p className="mt-4 text-xs uppercase tracking-widest text-[#006837] font-semibold">
            {t("thankYou.eyebrow")}
          </p>
          <h1 className="mt-2 font-serif-heading text-4xl sm:text-5xl font-black text-slate-900 leading-tight">
            {t("thankYou.title")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed">
            {t("thankYou.body")}
          </p>

          <div className="mt-10 text-left bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
            <h2 className="font-serif-heading text-xl font-bold text-slate-900">{t("thankYou.nextTitle")}</h2>
            <ul className="mt-4 space-y-4">
              {[
                { icon: Mail, text: t("thankYou.next1") },
                { icon: IdCard, text: t("thankYou.next2") },
                { icon: ShieldCheck, text: t("thankYou.next3") },
              ].map((item, i) => (
                <li key={i} className="flex gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-md bg-[#E8F5E9] text-[#006837] flex-shrink-0">
                    <item.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <p className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <Link
              to="/"
              data-testid="thank-you-back-home"
              className="inline-flex items-center gap-2 rounded-md bg-[#006837] hover:bg-[#00512b] px-6 py-3 text-sm font-semibold text-white shadow"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t("thankYou.cta")}
            </Link>
          </div>
        </div>
      </main>

      <footer className="bg-[#0B2545] text-slate-300 py-8 border-t-4 border-[#006837]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-xs text-center text-slate-400">
          {t("footer.copyright")} · {t("footer.legalRfc")}
        </div>
      </footer>
    </div>
  );
}
